/**
 * IFC class summary panel.
 *
 * Builds a clickable summary of the `ifc_class` values in the additive
 * element-info table. Clicking a class fills the hierarchy filter with that
 * class name so the user can see every element of that type in one step.
 */

import type { ElementInfoTable } from "./types";

export interface ClassBucket {
  readonly className: string;
  readonly count: number;
}

export interface ClassSummaryPanelOptions {
  /** Called when the user clicks a class bucket. */
  readonly onSelectClass: (className: string) => void;
}

function formatCount(value: number): string {
  return value.toLocaleString("en-US");
}

/**
 * Count elements by IFC class, treating a missing class as "(no class)".
 *
 * Counts are produced from `element_info` rows, which are aligned 1:1 with
 * `entry_count`. The summary is honest about how much of the model has no class
 * metadata.
 */
export function buildClassSummary(table: ElementInfoTable): readonly ClassBucket[] {
  const counts = new Map<string, number>();
  for (const entry of table.element_info) {
    const className = entry.ifc_class ?? "(no class)";
    counts.set(className, (counts.get(className) ?? 0) + 1);
  }

  const buckets: ClassBucket[] = [];
  for (const [className, count] of counts) {
    buckets.push({ className, count });
  }

  // Biggest first; ties broken alphabetically so the order is deterministic.
  buckets.sort((a, b) => {
    if (b.count !== a.count) {
      return b.count - a.count;
    }
    return a.className.localeCompare(b.className);
  });

  return buckets;
}

export function renderClassSummaryPanel(
  host: HTMLElement,
  table: ElementInfoTable,
  options: ClassSummaryPanelOptions,
): void {
  host.replaceChildren();

  const buckets = buildClassSummary(table);
  if (buckets.length === 0) {
    const empty = document.createElement("p");
    empty.className = "hint";
    empty.textContent = "クラス情報がありません。/ No class information.";
    host.append(empty);
    return;
  }

  const list = document.createElement("ul");
  list.className = "class-summary";

  for (const bucket of buckets) {
    const item = document.createElement("li");
    const hasClass = bucket.className !== "(no class)";

    if (hasClass) {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "class-summary-row";
      button.title = `「${bucket.className}」で階層をフィルタ / Filter hierarchy by ${bucket.className}`;
      button.addEventListener("click", () => {
        options.onSelectClass(bucket.className);
      });

      const name = document.createElement("span");
      name.className = "class-summary-name";
      name.textContent = bucket.className;

      const count = document.createElement("span");
      count.className = "class-summary-count";
      count.textContent = formatCount(bucket.count);

      button.append(name, count);
      item.append(button);
    } else {
      const row = document.createElement("div");
      row.className = "class-summary-row class-summary-row-inactive";

      const name = document.createElement("span");
      name.className = "class-summary-name";
      name.textContent = bucket.className;

      const count = document.createElement("span");
      count.className = "class-summary-count";
      count.textContent = formatCount(bucket.count);

      row.append(name, count);
      item.append(row);
    }

    list.append(item);
  }

  host.append(list);

  const total = document.createElement("p");
  total.className = "class-summary-total hint";
  const elementsWord = table.entry_count === 1 ? "element" : "elements";
  const classesWord = buckets.length === 1 ? "class" : "classes";
  total.textContent =
    `${formatCount(table.entry_count)} 要素 / ${formatCount(buckets.length)} クラス` +
    ` / ${formatCount(table.entry_count)} ${elementsWord}, ${formatCount(buckets.length)} ${classesWord}`;
  host.append(total);

  const nullCounts = table.null_counts;
  const missing: string[] = [];
  if (nullCounts.name > 0) missing.push(`name ${formatCount(nullCounts.name)}`);
  if (nullCounts.ifc_class > 0) missing.push(`class ${formatCount(nullCounts.ifc_class)}`);
  if (nullCounts.description > 0) missing.push(`description ${formatCount(nullCounts.description)}`);
  if (missing.length > 0) {
    const quality = document.createElement("p");
    quality.className = "class-summary-total hint";
    quality.textContent = `未設定属性 / Missing attributes: ${missing.join(", ")}`;
    host.append(quality);
  }
}
